import { Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { type FC, useState } from 'react';

import { type RouterNetworkResponse, type RouterNetworkSimSummary } from '@okvc/schema';

import { minutesOnSim } from './routerNetworkFormat.js';
import RouterSimCard from './RouterSimCard.js';
import SimSwitchConfirmDialog from './SimSwitchConfirmDialog.js';
import { RoutersApiKeys, useSwitchRouterSim } from '../../../api/index.js';
import { apiErrorMessage } from '../../../utils/api-error-message.js';

interface RouterSimCardsProps {
  routerId: string;
  network: RouterNetworkResponse;
  days: number;
  canSwitchSim: boolean;
}

const RouterSimCards: FC<RouterSimCardsProps> = ({ routerId, network, days, canSwitchSim }) => {
  const queryClient = useQueryClient();
  const [chosenSim, setChosenSim] = useState<RouterNetworkSimSummary | null>(null);
  const switchSim = useSwitchRouterSim(routerId);
  const isSwitching = network.router.networkState === 'switching';

  const closeDialog = () => {
    setChosenSim(null);
    switchSim.reset();
  };

  const confirmSwitch = () => {
    if (!chosenSim) {
      return;
    }
    switchSim.mutate(chosenSim.id, {
      onSuccess: () => {
        setChosenSim(null);
        void queryClient.invalidateQueries([RoutersApiKeys.Routers, routerId]);
      },
    });
  };

  return (
    <>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {network.sims.map((sim) => (
          <RouterSimCard
            key={sim.id}
            sim={sim}
            activeSince={network.router.activeSimSince}
            minutesOnSim={minutesOnSim(network.days, sim.id)}
            days={days}
            onUseSim={canSwitchSim ? () => setChosenSim(sim) : undefined}
            isSwitching={isSwitching}
          />
        ))}
      </Stack>
      <SimSwitchConfirmDialog
        sim={chosenSim}
        switching={switchSim.isLoading}
        switchError={switchSim.isError ? apiErrorMessage(switchSim.error, 'The SIM switch could not be started') : null}
        onConfirm={confirmSwitch}
        onCancel={closeDialog}
      />
    </>
  );
};

export default RouterSimCards;
